import * as React from 'react';
import * as _ from 'lodash';
import {
  Logging,
  ControlState,
  ControlStyleClass,
  renderEdit,
  renderSelect,
  stateProvinceAbbreviationToName,
  countryAbbreviationToName,
  Contact,
  ControlValidation,
  DisplayData,
  getCountries,
  getStateProvinces,
  validateEmail,
  validatePhone,
  validatePhoneExtension,
  validatePostalCode,
  DisplayDataStateProvince,
  stateProvinceAbbreviationToObject,
  getPhonePrefixForCountry,
  PermissionGroup,
  getCreatePermission,
  UserInfo,
  UserContext,
} from '@vcd-ecm-ui/Common';
import {
  VtxButton,
} from '@vertexinc/vtx-ui-react-component-library';
import '../../sidebyside-viewer.scss';
import SideBySideViewerContactTabProps from './sbsContactTabProps.interface';

const maxEmailLength = 100;
const maxAddressLength = 100;

const controlStyle: ControlStyleClass = {
  fieldHeader: 'sbsFieldHeader',
  fieldBase: 'sbsFieldBase',
  fieldEnabled: 'sbsFieldEnabled',
  fieldDisabled: 'sbsFieldDisabled',
  fieldValidation: 'sbsFieldValidation',
  fieldDropDown: 'sbsFieldDropDownBlack', // Special Style for Drop List
};

const controlValidation: ControlValidation = {
  requiredFields: ['contact-name'],
  emailFields: ['contact-email'],
  phoneFields: ['contact-phone', 'contact-fax'],
  phoneExtensionFields: ['contact-extension'],
  postalCodeFields: ['contact-postal-zip'],
  minMaxSize: [
    { field: 'contact-email', maxSize: maxEmailLength },
    { field: 'contact-address', maxSize: maxAddressLength },
    { field: 'contact-address-2', maxSize: maxAddressLength },
  ],
  customMessages: null,
};

const SideBySideViewerContactTab = ({
  certificate,
  editable,
  customerContacts,
  selectedContact,
  setSelectedContact,
  onSaveContact,
}: SideBySideViewerContactTabProps): JSX.Element => {
  const [addContact, setAddContact] = React.useState(false);

  const {
    customer,
    seller,
    form,
    contact,
    coverages,
  } = certificate;

  const [name, setName] = React.useState(contact ? contact.name : null);
  const [department, setDepartment] = React.useState(contact ? contact.department : null);
  const [address, setAddress] = React.useState(contact ? contact.address : null);
  const [address2, setAddress2] = React.useState(contact ? contact.address2 : null);
  const [city, setCity] = React.useState(contact ? contact.city : null);
  const [state, setState] = React.useState(contact ? stateProvinceAbbreviationToName(contact.state) : null);
  const [postalCode, setPostalCode] = React.useState(contact ? contact.postalCode : null);
  const [country, setCountry] = React.useState(contact ? countryAbbreviationToName(contact.country) : null);
  const [phone, setPhone] = React.useState(contact ? contact.phone : null);
  const [extension, setExtension] = React.useState(contact ? contact.phoneExtension : null);
  const [email, setEmail] = React.useState(contact ? contact.email : null);
  const [fax, setFax] = React.useState(contact ? contact.fax : null);
  const [phonePrefix, setPhonePrefix] = React.useState(getPhonePrefixForCountry(contact.country));

  const controlState: ControlState = {
    editable,
    disabled: !editable,
    readOnly: !editable,
  };

  const getCertificateContacts = (): DisplayData[] => {
    Logging.Debug('Certificate Contacts', customerContacts);

    // eslint-disable-next-line arrow-body-style
    return customerContacts?.map((customerContact: Contact) => {
      return {
        data: customerContact.uuid,
        display: customerContact.name || 'No Contact Name provided',
      };
    });
  };

  // Validate the Contact Values
  const validateContactValues = (): boolean => {
    let validated = true;

    // Validate Name
    if (!name) {
      validated = false;
    }

    // Validate Email
    if (!validateEmail(email)) {
      validated = false;
    }

    // Validate the Email
    if (email && (email.length > maxEmailLength)) {
      return false;
    }

    // Validate the Address
    if (address && (address.length > maxAddressLength)) {
      return false;
    }

    // Validate the Address 2
    if (address2 && (address2.length > maxAddressLength)) {
      return false;
    }

    // Validate Phone
    if (!validatePhone(phone)) {
      validated = false;
    }

    // Validate Fax
    if (!validatePhone(fax)) {
      validated = false;
    }

    // Validate Phone Extension
    if (!validatePhoneExtension(extension)) {
      validated = false;
    }

    // Postal Zip
    if (!validatePostalCode(postalCode, country)) {
      validated = false;
    }

    return validated;
  };

  // Add a New Contact
  const onAddContact = (): void => {
    setName(null);
    setDepartment(null);
    setAddress(null);
    setAddress2(null);
    setCity(null);
    setState(null);
    setPostalCode(null);
    setCountry(null);
    setPhone(null);
    setExtension(null);
    setFax(null);
    setEmail(null);

    setAddContact(true);
  };

  // Create the Contact. Pass to the Parent to Create.
  const onSaveButton = (): void => {
    const newContact: Contact = {
      name,
      address,
      address2,
      department,
      city,
      state,
      postalCode,
      country,
      phone,
      phoneExtension: extension,
      email,
      fax,
      primaryInd: false,
      emailSubscribedInd: true,
    };

    Logging.Info('Save Contact', newContact);

    onSaveContact(newContact);

    setAddContact(false);
  };

  // Country was Changed
  // eslint-disable-next-line arrow-body-style
  React.useEffect(() => {
    if (editable) {
      // Country was Changed.  Check to see if the State is still Valid
      if (country && state) {
        const displayDataGeo: DisplayDataStateProvince = stateProvinceAbbreviationToObject(state);

        // Clear the State if a differnt Country was picked
        if (displayDataGeo && (country !== displayDataGeo.country)) {
          setState(null);
        }
      } else if (!country && state) {
        setState(null);
      }

      // Set the Phone Prefix
      setPhonePrefix(getPhonePrefixForCountry(country));
    }
  }, [country]);

  // State was changed
  // eslint-disable-next-line arrow-body-style
  React.useEffect(() => {
    if (editable) {
      // State is Selected but not the Country.
      if (state && !country) {
        const displayDataGeo: DisplayDataStateProvince = stateProvinceAbbreviationToObject(state);

        // Picking the Country from the State
        if (displayDataGeo) {
          setCountry(displayDataGeo.country);
        }
      }
    }
  }, [state]);

  const saveContactEnabled = validateContactValues();

  // Set the Country used in Validation
  controlValidation.country = country;

  // Get the User Context
  const userContext: UserInfo = React.useContext(UserContext);

  // Get the Contacts Create Permission
  const permissionContactsCreate: boolean = getCreatePermission(userContext, PermissionGroup.Contacts);

  return (
    <>
      <div id="contact-information-tab" className="sbsContentLeftTabContent">
        {
          editable
          && (
            <>
              {renderSelect(66, 'select-contact', 'Contact', 'Select Contact', false, false,
                getCertificateContacts(), true, null, selectedContact, setSelectedContact, controlState, controlStyle, controlValidation)}
              {
                permissionContactsCreate && (
                  <div className="addContactLink field33">
                    <VtxButton
                      id="addContactButton"
                      onClick={onAddContact}
                      type="link"
                    >
                      Add Contact
                    </VtxButton>
                  </div>
                )
              }
              <div className="sbsContactEditInformation">
                If the contact you wish to assign to this certificate is not listed above, add a new contact to the customer record.
              </div>
            </>
          )
        }
        {
          (!editable || (editable && addContact))
          && (
            <>
              {renderEdit(50, 'contact-name', 'NAME', 'text', null,
                name, setName, controlState, controlStyle, controlValidation)}
              {renderEdit(50, 'contact-department', 'DEPARTMENT', 'text', null,
                department, setDepartment, controlState, controlStyle, controlValidation)}
              {renderEdit(100, 'contact-address', 'STREET ADDRESS', 'text', null,
                address, setAddress, controlState, controlStyle, controlValidation)}
              {renderEdit(100, 'contact-address-2', 'STREET ADDRESS 2', 'text', null,
                address2, setAddress2, controlState, controlStyle, controlValidation)}
              {renderEdit(50, 'contact-city', 'CITY', 'text', null,
                city, setCity, controlState, controlStyle, controlValidation)}
              {renderSelect(50, 'contact-province-state', 'STATE / PROVINCE', 'Select State / Province', false, false,
                getStateProvinces(country), true, null, state, setState, controlState, controlStyle, controlValidation)}
              {renderEdit(50, 'contact-postal-zip', 'POSTAL / ZIP CODE', 'text', null,
                postalCode, setPostalCode, controlState, controlStyle, controlValidation)}
              {renderSelect(50, 'customer-country', 'COUNTRY', 'Select Country', false, false,
                getCountries(), true, null, country, setCountry, controlState, controlStyle, controlValidation)}
              {renderEdit(50, 'contact-phone', 'PHONE NUMBER', 'tel', '(xxx) xxx-xxxx',
                phone, setPhone, controlState, controlStyle, controlValidation, phonePrefix)}
              {renderEdit(50, 'contact-extension', 'EXTENSION', 'text', null,
                extension, setExtension, controlState, controlStyle, controlValidation)}
              {renderEdit(50, 'contact-fax', 'FAX', 'tel', '(xxx) xxx-xxxx',
                fax, setFax, controlState, controlStyle, controlValidation, phonePrefix)}
              {renderEdit(50, 'contact-email', 'E-MAIL', 'email', null,
                email, setEmail, controlState, controlStyle, controlValidation)}
            </>
          )
        }
        {
          editable && addContact
          && (
            <>
              <div className="saveContactButton field100">
                <VtxButton
                  id="saveContactButton"
                  onClick={onSaveButton}
                  disabled={!saveContactEnabled}
                  type="primary"
                >
                  Save
                </VtxButton>
              </div>
            </>
          )
        }
      </div>
    </>
  );
};

export default SideBySideViewerContactTab;
